import { MainLayout } from '../../Layout/MainLayout';
import { Button, ButtonLink, Grid, Stack, TableContentIsland } from '@razz-team/ui-web';
import { PayoutTable } from './PayoutTable';
import { Route } from '../../../routes/account.payouts_.$payoutId';
import { usePayoutQuery, usePayoutStatementTransfersMutation } from '../../../api/payouts';
import { useTranslation } from 'react-i18next';
import { MainBreadcrumbs } from '../../Layout/MainBreadcrumbs';
import { useBoolean } from 'usehooks-ts';
import { AbortPayoutDialog } from './AbortPayoutDialog';
import { isPayoutAbortable, isPayoutEditable } from '../../../openapi/payout';
import { ConfirmPayoutDialog } from './ConfirmPayoutDialog';
import { useCallback } from 'react';

export function PayoutPage() {
  const { t } = useTranslation();
  const { createdAt } = Route.useSearch();
  const { payoutId } = Route.useParams();

  const payoutQuery = usePayoutQuery({ payoutId, createdAt });
  const payout = payoutQuery.data?.payout;
  const editable = isPayoutEditable(payout);
  const payable = editable && Boolean(payoutQuery.data?.transfers.length);
  const abortable = isPayoutAbortable(payout);
  const completed = payout?.state === 'COMPLETED';
  const paused = payout?.state === 'PAUSED';

  const cancelFlag = useBoolean(false);
  const confirmDialogIsOpened = useBoolean(false);

  const statementMutation = usePayoutStatementTransfersMutation({ payoutId, createdAt });

  const handleClickStatement = useCallback(() => {
    statementMutation.mutate();
  }, [statementMutation]);

  return (
    <MainLayout
      title={
        <MainBreadcrumbs
          title={payout?.name ?? ''}
          loading={payoutQuery.isPending}
          parent={{
            title: t('Payouts'),
            to: '/account/payouts',
          }}
        />
      }
      actions={
        editable ? (
          <Stack spacing={2} direction="row">
            {payable ? (
              <Button size="small" onClick={confirmDialogIsOpened.setTrue}>
                {t('Confirm and pay')}
              </Button>
            ) : null}
            <ButtonLink
              size="small"
              color="secondary"
              to="/account/payouts/$payoutId/create"
              params={{ payoutId }}
              search={{ createdAt }}
            >
              {t('Add payment')}
            </ButtonLink>
          </Stack>
        ) : abortable ? (
          <Stack spacing={2} direction="row">
            <Button color="secondary" size="small" onClick={cancelFlag.setTrue}>
              {t('Abort')}
            </Button>
            {paused ? (
              <Button size="small" onClick={confirmDialogIsOpened.setTrue}>
                {t('Execute')}
              </Button>
            ) : null}
          </Stack>
        ) : completed ? (
          <Button
            color="secondary"
            size="small"
            onClick={handleClickStatement}
            loading={statementMutation.isPending}
          >
            {t('Statement')}
          </Button>
        ) : null
      }
    >
      <Grid size={12}>
        <TableContentIsland>
          <PayoutTable editable={editable} />
        </TableContentIsland>
      </Grid>
      <AbortPayoutDialog
        payout={cancelFlag.value ? (payout ?? null) : null}
        onClose={cancelFlag.setFalse}
      />
      {payout && (
        <ConfirmPayoutDialog
          payout={payout}
          open={confirmDialogIsOpened.value}
          onClose={confirmDialogIsOpened.setFalse}
        />
      )}
    </MainLayout>
  );
}
